import React, { useState } from "react";
import { Button, Divider, Input, Snackbar, Dialog, List, ListItem, ListItemText } from "../../../tailwindcomponents";
import { Add as AddIcon } from '../../../components/icons';
import { Team } from "../../../types/maintenanceTeams";
import EmployeeSearch from "../../../components/EmployeeSearch/EmployeeSearch";
import MaintenanceTeamsApi from "../../../api/maintenanceTeams";

type AlertColor = 'success' | 'info' | 'warning' | 'error';

interface Props {
  teams: Team[];
  selectedTeam: Team | null;
  onSelectTeam: (team: Team) => void;
  onTeamCreated: () => void;
}

const ManageTeams: React.FC<Props> = ({
  teams,
  selectedTeam,
  onSelectTeam,
  onTeamCreated,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamLeaderId, setTeamLeaderId] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const handleOpenCreateDialog = () => {
    setTeamName("");
    setTeamLeaderId(0);
    setCreateDialogOpen(true);
  };

  const handleCloseDialog = (confirmed: boolean) => {
    setCreateDialogOpen(false);
    
    if (confirmed && teamName.trim() && teamLeaderId > 0) {
      const nameToCreate = teamName.trim();
      const leaderIdToCreate = teamLeaderId;
      setTeamName("");
      setTeamLeaderId(0);
      handleCreateTeam(nameToCreate, leaderIdToCreate);
    } else {
      setTeamName("");
      setTeamLeaderId(0);
    }
  };

  const handleCreateTeam = (name: string, leaderId: number) => {
    setIsCreating(true);
    MaintenanceTeamsApi.CreateTeam(name, leaderId)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Team created successfully");
          setSnackbarSeverity("success");
          onTeamCreated();
        } else {
          setSnackbarMessage(response.message || "Failed to create team");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while creating the team");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  return (
    <div>
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        className="mb-2"
        onClick={handleOpenCreateDialog}
      >
        Create Team
      </Button>

      <Divider className="mb-2" />

      <List dense>
        {teams.map((team) => (
          <ListItem key={team.Id} dense disableGutters className="p-0 mb-0.5">
            <button
              type="button"
              className={`flex w-full items-center text-left cursor-pointer rounded px-2 py-1 hover:bg-black/[0.04] border-none bg-transparent${selectedTeam?.Id === team.Id ? ' bg-primary/10' : ''}`}
              onClick={() => onSelectTeam(team)}
            >
              <ListItemText
                primary={team.TeamName}
                secondary={`${team?.TeamMembers?.length || 0} members`}
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
                className="mt-1 mb-1"
              />
            </button>
          </ListItem>
        ))}
      </List>

      <Dialog
        visible={createDialogOpen}
        max_width="sm"
        title="Create New Team"
        strict={true}
        show_options={true}
        onClose={handleCloseDialog}
        context={
          <div className="flex flex-wrap gap-6">
            <div className="w-full">
              <Input
                multiline={false}
                label_text="Team Name"
                value={teamName}
                help_text="Enter the name for the new team"
                required={true}
                onTextChange={(value: string) => setTeamName(value)}
                type="text"
                width={100}
                disabled={isCreating}
              />
            </div>
            <div className="w-full">
              <EmployeeSearch
                place_holder="Select Team Leader"
                onSelectedEmployee={(employeeId: number) => setTeamLeaderId(employeeId)}
                required={true}
                fullUser={false}
                disabled={isCreating}
              />
            </div>
          </div>
        }
      />

      <Snackbar
        onSnackBarClose={() => setSnackbarMessage("")}
        open={snackbarMessage.length > 0}
        message={snackbarMessage}
        autoHide={true}
        horizontalPosition="right"
        severity={snackbarSeverity}
      />
    </div>
  );
};

export default ManageTeams;
